import { DisclosureFilter } from "../disclosureFilter/DisclosureFilter.tsx";
import { InterestType } from "../../../../../../stores/types/personasTypes.tsx";
import { useMemo, useState } from "react";
import {
  RevButton,
  RevChip,
  RevEmptyState,
  RevSpinner,
} from "@revmatics/catalyst";
import { useAppSelector } from "../../../../../../stores/hooks/hooks.ts";

type Props = {
  selectedCommunity: Set<number>;
  selectedInterests: Set<string>;
  setSelectedInterests: (value: Set<string>) => void;
  setSelectedParentInterest: (value: InterestType | null) => void;
  selectedParentInterest: InterestType | null;
  selectedInterestsCommunity: Set<string>;
  setSelectedInterestsCommunity: (value: Set<string>) => void;
  setSelectedCommunity: (value: Set<number>) => void;
  isFirstLoading: boolean;
};
export const InterestDisclosureFilter = ({
  selectedCommunity,
  selectedInterests,
  setSelectedInterests,
  setSelectedParentInterest,
  selectedParentInterest,
  selectedInterestsCommunity,
  setSelectedInterestsCommunity,
  setSelectedCommunity,
  isFirstLoading,
}: Props) => {
  const [openSubInterest, setOpenSubInterest] = useState(false);
  const isLoadingCommunity = useAppSelector(
    (state) => state.discoverCommunities.isLoadingCommunities,
  );
  const selectedPersona = useAppSelector(
    (state) => state.discoverCommunities.selectedPersona,
  );
  const parentInterestsFiltered = useMemo(() => {
    return selectedPersona?.parent_interests?.filter(
      (interest) => interest.children.length > 0,
    );
  }, [selectedPersona?.parent_interests]);

  return (
    <DisclosureFilter
      title="Community Interest Groups"
      tooltip="Select one or more interest"
      selectedList={
        selectedParentInterest
          ? [selectedParentInterest.taxonomy_type, ...[...selectedInterests]]
          : [...selectedInterests]
      }
    >
      {parentInterestsFiltered &&
      parentInterestsFiltered.length > 0 &&
      selectedPersona?.parent_interests &&
      selectedPersona?.parent_interests.length > 0 ? (
        <div className="p-2 rounded-lg bg-monochrome-910 flex flex-col gap-3">
          {isFirstLoading ? (
            <RevSpinner size="medium" color="primary" />
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                {parentInterestsFiltered.map((interest) => (
                  <RevChip
                    key={`discover-community-parent-interests-${interest.id}`}
                    handleClick={() => {
                      const isSelectedParent =
                        interest.id === selectedParentInterest?.id;
                      setSelectedParentInterest(
                        isSelectedParent ? null : interest,
                      );
                      setSelectedInterests(new Set());
                    }}
                    text={interest.taxonomy_type}
                    selected={interest.id === selectedParentInterest?.id}
                  />
                ))}
              </div>
              {selectedParentInterest &&
                selectedParentInterest.children.length > 0 && (
                  <RevButton
                    displayAs="link"
                    underline
                    text={
                      openSubInterest
                        ? "Hide Community Interests"
                        : "Show Community Interests"
                    }
                    className="w-fit"
                    linkFontSize="small"
                    handleClick={() => setOpenSubInterest(!openSubInterest)}
                  />
                )}
              {selectedParentInterest &&
                selectedParentInterest.children.length > 0 && (
                  <div
                    className={`flex-col gap-y-3 ${openSubInterest ? "flex" : "hidden"}`}
                  >
                    <div className="flex gap-3 items-center">
                      <h6>Community Interests</h6>
                      {isLoadingCommunity ? (
                        <RevSpinner size="medium" color="primary" />
                      ) : (
                        <RevButton
                          displayAs="link"
                          underline={false}
                          text={
                            (selectedInterests.size === 0 &&
                              selectedInterestsCommunity.size === 0) ||
                            (selectedInterests.size > 0 &&
                              selectedInterestsCommunity.size === 0 &&
                              selectedCommunity.size === 0)
                              ? "Select All"
                              : "Deselect All"
                          }
                          className="w-fit !pt-0"
                          linkFontSize="small"
                          handleClick={() => {
                            if (
                              (selectedInterests.size === 0 &&
                                selectedInterestsCommunity.size === 0) ||
                              (selectedInterests.size > 0 &&
                                selectedInterestsCommunity.size === 0 &&
                                selectedCommunity.size === 0)
                            ) {
                              setSelectedInterests(
                                new Set(selectedParentInterest.children),
                              );
                            } else {
                              setSelectedInterests(new Set());
                            }
                            if (selectedInterestsCommunity.size > 0) {
                              setSelectedInterestsCommunity(new Set());
                              setSelectedCommunity(new Set());
                            }
                          }}
                        />
                      )}
                    </div>
                    <div className={`flex flex-wrap gap-2`}>
                      {selectedParentInterest.children.map((interest) => (
                        <RevChip
                          key={`discover-community-child-interests-${interest}`}
                          handleClick={() => {
                            const updatedSet: Set<string> =
                              selectedInterestsCommunity.size > 0
                                ? new Set(selectedInterestsCommunity)
                                : selectedCommunity.size > 0
                                  ? new Set(selectedInterests)
                                  : new Set();
                            if (
                              updatedSet.has(interest) &&
                              (selectedInterestsCommunity.size > 0
                                ? selectedInterestsCommunity.has(interest)
                                : true)
                            ) {
                              updatedSet.delete(interest);
                            } else {
                              updatedSet.add(interest);
                            }
                            if (selectedInterestsCommunity.size > 0) {
                              setSelectedInterestsCommunity(new Set());
                              setSelectedCommunity(new Set());
                            }
                            setSelectedInterests(updatedSet);
                          }}
                          text={interest}
                          selected={
                            selectedInterestsCommunity.size > 0 ||
                            selectedCommunity.size === 0
                              ? selectedInterestsCommunity.has(interest)
                              : selectedInterests.has(interest)
                          }
                        />
                      ))}
                    </div>
                  </div>
                )}
            </>
          )}
        </div>
      ) : (
        <RevEmptyState title="No Interests" text="" />
      )}
    </DisclosureFilter>
  );
};
